import React from "react";

import { Button } from "@mui/material";
import { useDropzone } from "react-dropzone";

import uploadIcon from "../../../assets/img/uploadIcon.svg";
import "../../../css/inputDrop.css";

interface InputDropProps {
  maxSizeInMB?: number;
  callbackDrop: (_acceptedFiles: File[], _fileRejections: any[]) => void;
  height?: string;
}
const InputDrop: React.FC<InputDropProps> = ({ maxSizeInMB = 25, callbackDrop, height }) => {
  // Helper function to extract files from DataTransfer
  const extractFilesFromDataTransfer = (dt: DataTransfer): File[] => {
    const files: File[] = [];
    if (dt.files && dt.files.length > 0) {
      files.push(...Array.from(dt.files));
    }
    return files;
  };

  // Helper function to extract files from input element
  const extractFilesFromInput = (target: EventTarget): File[] => {
    const files: File[] = [];
    const inputFiles = (target as HTMLInputElement).files;
    if (inputFiles) {
      files.push(...Array.from(inputFiles));
    }
    return files;
  };
  // Wrapper to handle Edge compatibility by converting FileSystemFileHandle to File
  const handleDrop = React.useCallback(
    (acceptedFiles: File[], fileRejections: any[]) => {
      Promise.all(
        acceptedFiles.map(async (file) => {
          // Check if this is a FileSystemFileHandle or has getFile method
          if (file && typeof (file as any).getFile === "function") {
            try {
              // Try to get the actual File object
              return await (file as any).getFile();
            } catch (error) {
              // If getFile fails (Edge restriction), return the file as-is
              // It should still work as a File-like object
              console.warn("Failed to access file handle, using file object directly:", error);
              return file;
            }
          }
          return file;
        })
      )
        .then((processedFiles) => {
          callbackDrop(processedFiles, fileRejections);
        })
        .catch((error) => {
          console.error("Error processing dropped files:", error);
          callbackDrop(acceptedFiles, fileRejections);
        });
    },
    [callbackDrop]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.ms-powerpoint": [".ppt", ".pptx"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/txt": [".txt"],
      "text/csv": [".csv"],
    },
    multiple: true,
    useFsAccessApi: false, // Disable File System Access API to ensure compatibility with Edge
    getFilesFromEvent: async (event) => {
      // Custom file extraction to bypass FileSystemFileHandle in Edge
      const files: File[] = [];
      try {
        if ("dataTransfer" in event && event.dataTransfer) {
          const dt = event.dataTransfer;
          files.push(...extractFilesFromDataTransfer(dt));
        } else if ("target" in event && event.target) {
          const target = event.target as HTMLInputElement;
          if (target.files) {
            files.push(...extractFilesFromInput(target));
          }
        }
      } catch (error) {
        console.warn("Error extracting files from event, falling back to default:", error);
        // Return empty array to let react-dropzone handle it with default behavior
        return [];
      }

      return files;
    },
  });
  return (
    <div
      {...getRootProps()}
      className="flex flow-column drop-input cursor-pointer justify-center"
      style={height ? { height } : {}}
    >
      <input {...getInputProps()} data-testid="dropzone" />
      {isDragActive ? (
        <div className="margin-auto">
          <span className="font-14 weight-700">Drop your files here</span>
        </div>
      ) : (
        <div className="flex flow-column gap-15 ">
          <div className="flex gap-15 width-100 justify-center">
            <div className="flex gap-5 align-items-center ">
              <img src={uploadIcon} alt="Upload icon" />
              <span className="font-14 weight-700">Drag & Drop</span>
            </div>
            <div className="vertical-separator" />
            <Button className="button-kg green">Browse Files</Button>
          </div>
          <div
            className="flex flow-column"
            style={{
              alignItems: "center",
            }}
          >
            <span className="font-14">Supported file types: pdf, docx,</span>
            <span className="font-14">pptx, xlsx, csv, txt.</span>
            <span className="font-14">The total size of all files should not</span>
            <span className="font-14"> exceed {maxSizeInMB}MB</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InputDrop;
